/*
 * MEGAKRUNCH PATCH BAY download page (sections/mk-patchbay-download.liquid)
 *
 * Resolves the current installer filenames from the PATCH BAY release feed
 * (the same electron-updater feed the app updates itself from) so the page
 * never hardcodes a version. When a new release is distributed the feed
 * pointer changes and this page follows with zero theme edits.
 *
 *   public feed  .../patchbay/latest.yml       + latest-mac.yml
 *   beta feed    .../patchbay/beta/latest.yml  + latest-mac.yml   (?channel=beta)
 *
 * Hosts: the feed and the installers are read through the CDN,
 * updates.megakrunch.com (CloudFront in front of the release bucket, since
 * 2026-09-23; installer repo docs/cloudfront-downloads.md). The pointers are
 * never cached there, so the page can never see a stale version. If a CDN
 * pointer read fails (network, CORS, HTTP error, or a body that is not a
 * feed), this page load switches to the S3 origin for every read and every
 * button, and says so in the console. Nothing is ever deleted from S3.
 *
 * Feed layout: megakrunch-installer docs/release-and-update.md. Both hosts
 * need to answer CORS for GET from the storefront origin (both answer *), or
 * the fetch below fails and the page shows its error state.
 *
 * SAFETY RULE: the feed is outside-the-theme data. Nothing from it is ever
 * written as HTML. Filenames must pass SAFE_FILENAME and are appended to one
 * of the two fixed base URLs, so a tampered feed cannot point a button at
 * another host. Versions and sizes are written with textContent only.
 *
 * The pure functions are exported for node (scripts/test-patchbay-download.test.js).
 */
(function (root) {
  'use strict';

  var FEED_BASE = 'https://updates.megakrunch.com/patchbay/';
  var ORIGIN_BASE = 'https://megakrunch-patchbay-releases.s3.us-east-2.amazonaws.com/patchbay/';
  var SAFE_FILENAME = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
  var SAFE_VERSION = /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/;

  function unquote(value) {
    return String(value).trim().replace(/^['"]|['"]$/g, '');
  }

  // Minimal reader for electron-builder's latest*.yml. Not a YAML parser: it
  // reads the top-level `version` and the `files` list (url + size), which is
  // all the page needs. Returns null when the text is not a usable feed.
  function parseFeed(text) {
    if (typeof text !== 'string') return null;
    var version = null;
    var files = [];
    var current = null;
    var lines = text.split(/\r?\n/);
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var m;
      if ((m = /^version:\s*(.+)$/.exec(line))) {
        version = unquote(m[1]);
      } else if ((m = /^\s+-\s+url:\s*(.+)$/.exec(line))) {
        current = { url: unquote(m[1]), size: null };
        files.push(current);
      } else if (current && (m = /^\s+size:\s*(\d+)\s*$/.exec(line))) {
        current.size = parseInt(m[1], 10);
      } else if (/^\S/.test(line)) {
        current = null;
      }
    }
    if (!version || !SAFE_VERSION.test(version)) return null;
    files = files.filter(function (f) { return SAFE_FILENAME.test(f.url); });
    return { version: version, files: files };
  }

  // The human download for an OS. On the Mac feed the updater payload (.zip)
  // is `path`; the .dmg only appears in files[], so match by extension.
  function pickInstaller(feed, os) {
    if (!feed || !feed.files) return null;
    var ext = os === 'mac' ? '.dmg' : os === 'win' ? '.exe' : null;
    if (!ext) return null;
    for (var i = 0; i < feed.files.length; i++) {
      var name = feed.files[i].url;
      if (name.toLowerCase().slice(-ext.length) === ext) return feed.files[i];
    }
    return null;
  }

  // semver compare: -1, 0, 1. A pre-release sorts below its release
  // (1.0.0-beta.1 < 1.0.0), which is what keeps the public buttons shut
  // until the feed serves a real 1.0.0.
  function compareVersions(a, b) {
    var pa = String(a).split('-');
    var pb = String(b).split('-');
    var na = pa[0].split('.');
    var nb = pb[0].split('.');
    for (var i = 0; i < 3; i++) {
      var x = parseInt(na[i], 10) || 0;
      var y = parseInt(nb[i], 10) || 0;
      if (x !== y) return x < y ? -1 : 1;
    }
    var ra = pa.slice(1).join('-');
    var rb = pb.slice(1).join('-');
    if (!ra && !rb) return 0;
    if (!ra) return 1;
    if (!rb) return -1;
    var ia = ra.split('.');
    var ib = rb.split('.');
    for (var j = 0; j < Math.max(ia.length, ib.length); j++) {
      if (ia[j] === undefined) return -1;
      if (ib[j] === undefined) return 1;
      var an = /^\d+$/.test(ia[j]);
      var bn = /^\d+$/.test(ib[j]);
      if (an && bn) {
        var p = parseInt(ia[j], 10);
        var q = parseInt(ib[j], 10);
        if (p !== q) return p < q ? -1 : 1;
      } else if (an !== bn) {
        return an ? -1 : 1;
      } else if (ia[j] !== ib[j]) {
        return ia[j] < ib[j] ? -1 : 1;
      }
    }
    return 0;
  }

  // 'mac' | 'win' | 'other'. iPadOS reports a Mac platform, so a touch
  // "Mac" is treated as other (the app cannot run there).
  function detectOS(nav) {
    nav = nav || {};
    var platform = String((nav.userAgentData && nav.userAgentData.platform) || nav.platform || '');
    var ua = String(nav.userAgent || '');
    if (/android|iphone|ipad|ipod/i.test(ua)) return 'other';
    if (/mac/i.test(platform) || /macintosh|mac os x/i.test(ua)) {
      return (nav.maxTouchPoints || 0) > 1 ? 'other' : 'mac';
    }
    if (/win/i.test(platform) || /windows/i.test(ua)) return 'win';
    return 'other';
  }

  function resolveChannel(search, betaEnabled) {
    if (!betaEnabled) return 'public';
    return /(?:^|[?&])channel=beta(?:&|$)/.test(String(search || '')) ? 'beta' : 'public';
  }

  // Where the visitor came from, for the GA4 event. Internal pointers link
  // here with ?ref=account|product|footer (not UTMs: a UTM on an internal
  // link would overwrite the session's real attribution).
  function resolvePlacement(search) {
    var m = /(?:^|[?&])ref=([a-z0-9_-]{1,24})(?:&|$)/i.exec(String(search || ''));
    return m ? m[1].toLowerCase() : 'direct';
  }

  // Only the two known hosts; anything else means the CDN.
  function knownBase(base) {
    return base === ORIGIN_BASE ? ORIGIN_BASE : FEED_BASE;
  }

  function feedUrl(channel, os, base) {
    return knownBase(base) + (channel === 'beta' ? 'beta/' : '') + (os === 'mac' ? 'latest-mac.yml' : 'latest.yml');
  }

  function installerUrl(channel, filename, base) {
    if (!SAFE_FILENAME.test(String(filename))) return null;
    return knownBase(base) + (channel === 'beta' ? 'beta/' : '') + filename;
  }

  // Reads feeds for one page load: one request per feed file, however many
  // download blocks the page has. Starts on the CDN. The first CDN failure
  // retries that read from the S3 origin and moves every later read (and,
  // through base(), every button) to the origin for the rest of the page load.
  // fetchFn and log are injected so node can test this without a network.
  function createFeedReader(fetchFn, log) {
    var cache = {};
    var onOrigin = false;

    function read(url) {
      return fetchFn(url, { cache: 'no-store', credentials: 'omit' })
        .then(function (res) {
          if (!res.ok) throw new Error('feed_http_' + res.status);
          return res.text();
        })
        .then(function (text) {
          var feed = parseFeed(text);
          if (!feed) throw new Error('feed_unreadable');
          return feed;
        });
    }

    function request(channel, os) {
      if (onOrigin) return read(feedUrl(channel, os, ORIGIN_BASE));
      var cdnUrl = feedUrl(channel, os, FEED_BASE);
      return read(cdnUrl).catch(function (err) {
        if (!onOrigin && log) {
          log('[PATCH BAY] ' + cdnUrl + ' failed (' + ((err && err.message) || 'error') +
            '); reading the feed and downloads from ' + ORIGIN_BASE + ' for this page load.');
        }
        onOrigin = true;
        return read(feedUrl(channel, os, ORIGIN_BASE));
      });
    }

    return {
      fetchFeed: function (channel, os) {
        var key = channel + '/' + os;
        if (!cache[key]) cache[key] = request(channel, os);
        return cache[key];
      },
      base: function () { return onOrigin ? ORIGIN_BASE : FEED_BASE; }
    };
  }

  function formatSize(bytes) {
    if (!bytes || bytes < 0) return '';
    return Math.round(bytes / 1048576) + ' MB';
  }

  // 'ready' when the channel's build may be offered. The public channel stays
  // 'pending' until the feed version reaches minPublic (section setting), so
  // this page can sit on the live theme before launch and unlock itself the
  // moment the public feed flips to 1.0.0.
  function gateState(channel, version, minPublic) {
    if (channel === 'beta') return 'ready';
    if (!minPublic || !SAFE_VERSION.test(minPublic)) return 'ready';
    return compareVersions(version, minPublic) >= 0 ? 'ready' : 'pending';
  }

  // A page can have more than one download block: the hero, and the "Get it"
  // section further down. Only the hero carries the channel settings (beta link,
  // minimum public version); a follower block reads them from the hero, so the
  // two can never disagree about which build is on offer. Own values win when a
  // block has them. Values are the raw attribute strings, null when absent.
  function inheritConfig(own, primary) {
    var p = primary || {};
    var o = own || {};
    return {
      betaEnabled: o.betaEnabled != null ? o.betaEnabled : (p.betaEnabled != null ? p.betaEnabled : 'false'),
      minPublic: o.minPublic != null ? o.minPublic : (p.minPublic != null ? p.minPublic : '')
    };
  }

  var api = {
    FEED_BASE: FEED_BASE,
    ORIGIN_BASE: ORIGIN_BASE,
    createFeedReader: createFeedReader,
    inheritConfig: inheritConfig,
    parseFeed: parseFeed,
    pickInstaller: pickInstaller,
    compareVersions: compareVersions,
    detectOS: detectOS,
    resolveChannel: resolveChannel,
    resolvePlacement: resolvePlacement,
    feedUrl: feedUrl,
    installerUrl: installerUrl,
    formatSize: formatSize,
    gateState: gateState
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }

  // ---------------------------------------------------------------- browser

  function track(params) {
    try {
      if (typeof root.gtag === 'function') root.gtag('event', 'patchbay_download', params);
    } catch (e) { /* analytics must never block a download */ }
    try {
      if (root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function') {
        root.Shopify.analytics.publish('mk_patchbay_download', params);
      }
    } catch (e) { /* same */ }
  }

  var feedReader = createFeedReader(function (url, opts) { return root.fetch(url, opts); }, function (msg) {
    try { if (root.console) root.console.warn(msg); } catch (e) { /* logging must never block a download */ }
  });
  var fetchFeed = feedReader.fetchFeed;

  function readConfig(el) {
    return el ? { betaEnabled: el.getAttribute('data-beta-enabled'), minPublic: el.getAttribute('data-min-public-version') } : null;
  }

  function init(el, primary) {
    // The hero and the "Get it" section each include this script; bind a block once.
    if (el.getAttribute('data-mk-pb-bound')) return;
    el.setAttribute('data-mk-pb-bound', '1');
    var config = inheritConfig(readConfig(el), readConfig(primary));
    var channel = resolveChannel(root.location.search, config.betaEnabled === 'true');
    var placement = resolvePlacement(root.location.search);
    // Which download block on the page was clicked: the hero, or "Get it" further down.
    var block = el.getAttribute('data-block') || 'hero';
    var minPublic = config.minPublic;
    var os = detectOS(root.navigator);

    el.setAttribute('data-channel', channel);
    el.setAttribute('data-os', os);

    function setState(state) { el.setAttribute('data-state', state); }

    Promise.all([fetchFeed(channel, 'mac'), fetchFeed(channel, 'win')]).then(function (results) {
      var byOs = { mac: results[0], win: results[1] };
      var state = 'ready';
      ['mac', 'win'].forEach(function (key) {
        if (gateState(channel, byOs[key].version, minPublic) !== 'ready') state = 'pending';
      });
      if (state === 'pending') { setState('pending'); return; }

      var wired = 0;
      // Both feeds have settled here, so base() already reflects any fallback.
      var base = feedReader.base();
      ['mac', 'win'].forEach(function (key) {
        var btn = el.querySelector('[data-mk-pb-download="' + key + '"]');
        var meta = el.querySelector('[data-mk-pb-meta="' + key + '"]');
        var file = pickInstaller(byOs[key], key);
        var url = file && installerUrl(channel, file.url, base);
        if (!btn || !url) return;
        btn.setAttribute('href', url);
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('tabindex');
        if (key === os) btn.classList.add('mk-patchbay__btn--primary');
        if (meta) {
          // Machine data (version, size) in Martian Mono; the words after it stay
          // Erbaum (UI style guide, text hierarchy level 6). Built with
          // textContent only: nothing from the feed is ever parsed as HTML.
          var bits = ['v' + byOs[key].version];
          var size = formatSize(file.size);
          if (size) bits.push(size);
          var data = root.document.createElement('span');
          data.className = 'mk-patchbay__data';
          data.textContent = bits.join(' · ');
          var suffix = meta.getAttribute('data-suffix');
          meta.textContent = '';
          meta.appendChild(data);
          if (suffix) meta.appendChild(root.document.createTextNode(' · ' + suffix));
        }
        btn.addEventListener('click', function () {
          track({ os: key, version: byOs[key].version, channel: channel, placement: placement, detected_os: os, block: block });
        });
        wired++;
      });
      setState(wired === 2 ? 'ready' : 'error');
    }).catch(function (err) {
      // The message only (feed_http_503, Failed to fetch), never a response body.
      try { if (root.console) root.console.warn('[PATCH BAY] download buttons not wired: ' + ((err && err.message) || 'error')); } catch (e) { /* never block */ }
      setState('error');
    });
  }

  function boot() {
    var nodes = root.document.querySelectorAll('[data-mk-patchbay]');
    var primary = root.document.querySelector('[data-mk-patchbay][data-min-public-version]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i], primary);
  }

  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
